import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Car, CheckCircle2, AlertCircle, CreditCard, Building, Shield, MapPin, Clock, ArrowLeft, ArrowRight, Users, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';
import { insurancePackages, paymentMethods, pickupLocations, extraOptions } from '../mockData';
import { trackBookingSubmission } from '../utils/tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSystem = ({ t, preselectedCar, language }) => {
  const [stage, setStage] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    carId: preselectedCar?.id?.toString() || '',
    pickupDate: null,
    returnDate: null,
    pickupLocation: 'coresi',
    dropoffLocation: 'coresi',
    differentDropoff: false,
    pickupTime: '10:00',
    dropoffTime: '10:00',
    insurancePackage: 'limited',
    paymentMethod: 'stripe',
    selectedExtras: []
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [cars, setCars] = useState([]);
  const [availableCars, setAvailableCars] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cars/available`);
        if (response.ok) {
          const data = await response.json();
          data.sort((a, b) => a.pricePerDay - b.pricePerDay);
          setCars(data);
          setAvailableCars(data);
        }
      } catch (error) {
        console.error('Failed to fetch cars:', error);
      }
    };
    fetchCars();
  }, []);

  // Check availability when moving to stage 2
  const checkAvailability = async () => {
    if (formData.pickupDate && formData.returnDate) {
      setCheckingAvailability(true);
      try {
        const startDate = format(formData.pickupDate, 'yyyy-MM-dd');
        const endDate = format(formData.returnDate, 'yyyy-MM-dd');
        const response = await fetch(`${BACKEND_URL}/api/cars/check-availability`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startDate, endDate })
        });
        if (response.ok) {
          const carsWithAvailability = await response.json();
          const available = carsWithAvailability.filter(car => car.availableForDates);
          available.sort((a, b) => a.pricePerDay - b.pricePerDay);
          setAvailableCars(available);
        }
      } catch (error) {
        console.error('Failed to check availability:', error);
        setAvailableCars(cars);
      } finally {
        setCheckingAvailability(false);
      }
    }
  };

  // Calculate price breakdown
  useEffect(() => {
    if (formData.carId && formData.pickupDate && formData.returnDate) {
      const car = cars.find(c => c.id === parseInt(formData.carId));
      if (car) {
        const diffTime = Math.abs(formData.returnDate - formData.pickupDate);
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (days > 0) {
          const carTotal = car.pricePerDay * days;
          const insurancePricing = car.insurancePricing || { none: 0, limited: 15, premium: 20 };
          const insurancePricePerDay = insurancePricing[formData.insurancePackage] || 0;
          const insuranceTotal = insurancePricePerDay * days;
          const pickupLoc = pickupLocations.find(l => l.id === formData.pickupLocation);
          const dropoffLoc = pickupLocations.find(l => l.id === formData.dropoffLocation);
          const locationFees = (pickupLoc?.price || 0) + (dropoffLoc?.price || 0);
          const extrasFees = formData.selectedExtras.reduce((total, extraId) => {
            const extra = extraOptions.find(e => e.id === extraId);
            return total + (extra?.price || 0);
          }, 0);
          const total = carTotal + insuranceTotal + locationFees + extrasFees;
          const deposits = car.deposits || { none: 500, limited: 300, premium: 0 };
          const deposit = deposits[formData.insurancePackage] || 0;
          setPriceBreakdown({ days, carPricePerDay: car.pricePerDay, carTotal, insurancePricePerDay, insuranceTotal, locationFees, extrasFees, total, deposit });
        }
      }
    } else {
      setPriceBreakdown(null);
    }
  }, [formData.carId, formData.pickupDate, formData.returnDate, formData.insurancePackage, formData.pickupLocation, formData.dropoffLocation, formData.selectedExtras, cars]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.fullName || !formData.email || !formData.phone || !formData.carId || !formData.pickupDate || !formData.returnDate) {
      setError(t.booking.error);
      return;
    }
    if (!priceBreakdown) {
      setError(language === 'ro' ? 'Nu se poate calcula prețul.' : 'Unable to calculate price.');
      return;
    }
    setLoading(true);
    try {
      const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      const newBooking = {
        id: Date.now(),
        ...formData,
        pickupDate: formData.pickupDate.toISOString(),
        returnDate: formData.returnDate.toISOString(),
        carName: cars.find(c => c.id === parseInt(formData.carId))?.name,
        priceBreakdown,
        createdAt: new Date().toISOString()
      };
      bookings.push(newBooking);
      localStorage.setItem('bookings', JSON.stringify(bookings));
      try {
        await fetch(`${BACKEND_URL}/api/bookings/notify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: formData.fullName, email: formData.email, phone: formData.phone,
            carName: cars.find(c => c.id === parseInt(formData.carId))?.name,
            pickupDate: formData.pickupDate.toISOString(), returnDate: formData.returnDate.toISOString(),
            pickupLocation: pickupLocations.find(l => l.id === formData.pickupLocation)?.nameEn,
            dropoffLocation: pickupLocations.find(l => l.id === formData.dropoffLocation)?.nameEn,
            pickupTime: formData.pickupTime, dropoffTime: formData.dropoffTime,
            insurancePackage: formData.insurancePackage, paymentMethod: formData.paymentMethod,
            selectedExtras: formData.selectedExtras.map(id => extraOptions.find(e => e.id === id)?.nameEn).filter(Boolean),
            priceBreakdown
          })
        });
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }
      trackBookingSubmission(priceBreakdown.total, 'EUR');
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setStage(1);
        setFormData({ fullName: '', email: '', phone: '', carId: '', pickupDate: null, returnDate: null, pickupLocation: 'coresi', dropoffLocation: 'coresi', differentDropoff: false, pickupTime: '10:00', dropoffTime: '10:00', insurancePackage: 'limited', paymentMethod: 'stripe', selectedExtras: [] });
      }, 5000);
    } catch (error) {
      setError(error.message || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBooking = () => {
    setTimeout(() => {
      const el = document.getElementById('booking');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const goToStage2 = async () => {
    setError('');
    if (!formData.pickupDate || !formData.returnDate) {
      setError(language === 'ro' ? 'Selectează datele de ridicare și returnare.' : 'Please select pickup and return dates.');
      return;
    }
    await checkAvailability();
    setStage(2);
    scrollToBooking();
  };

  const selectCar = (carId) => {
    setFormData(prev => ({ ...prev, carId: carId.toString() }));
  };

  const goToStage3 = () => {
    setError('');
    if (!formData.carId) {
      setError(language === 'ro' ? 'Selectează o mașină.' : 'Please select a car.');
      return;
    }
    setStage(3);
    scrollToBooking();
  };

  const isRo = language === 'ro';

  // Stage indicator
  const StageIndicator = () => (
    <div className="flex items-center justify-center mb-8 gap-2">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            s === stage ? 'bg-[#10B981] text-white' : s < stage ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-gray-200 text-gray-400'
          }`}>
            {s < stage ? <CheckCircle2 size={16} /> : s}
          </div>
          {s < 3 && <div className={`w-8 sm:w-12 h-0.5 ${s < stage ? 'bg-[#10B981]' : 'bg-gray-200'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <section id="booking" className="py-20 sm:py-28 bg-[#0D1B3E]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
            {isRo ? 'Rezervare Rapidă' : 'Quick Booking'}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {t.booking.title}
          </h2>
        </div>

        <div className={`mx-auto ${stage === 2 ? 'max-w-5xl' : 'max-w-2xl'}`}>
          <div className="bg-white rounded-xl shadow-2xl">
            <div className="p-6 sm:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="mx-auto text-[#10B981] mb-6" size={56} strokeWidth={1.5} />
                  <p className="font-body text-lg text-gray-700">{t.booking.success}</p>
                </div>
              ) : (
                <>
                  <StageIndicator />

                  {/* ===== STAGE 1: Dates & Location ===== */}
                  {stage === 1 && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {t.booking.pickupDate}
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" data-testid="select-pickup-date"
                                className="w-full justify-start text-left font-body bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 rounded-lg h-12">
                                <CalendarIcon className="mr-3 h-4 w-4 text-[#10B981]" />
                                {formData.pickupDate ? format(formData.pickupDate, 'PPP') : <span className="text-gray-400">{t.booking.pickupDate}</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-white border-gray-200 rounded-lg shadow-lg">
                              <Calendar mode="single" selected={formData.pickupDate}
                                onSelect={(date) => setFormData({...formData, pickupDate: date})}
                                initialFocus disabled={(date) => date < new Date()} className="bg-white text-gray-900" />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {t.booking.returnDate}
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" data-testid="select-return-date"
                                className="w-full justify-start text-left font-body bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 rounded-lg h-12">
                                <CalendarIcon className="mr-3 h-4 w-4 text-[#10B981]" />
                                {formData.returnDate ? format(formData.returnDate, 'PPP') : <span className="text-gray-400">{t.booking.returnDate}</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-white border-gray-200 rounded-lg shadow-lg">
                              <Calendar mode="single" selected={formData.returnDate}
                                onSelect={(date) => setFormData({...formData, returnDate: date})}
                                initialFocus disabled={(date) => date < (formData.pickupDate || new Date())} className="bg-white text-gray-900" />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      {/* Location */}
                      <div>
                        <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                          {isRo ? 'Locație Ridicare / Predare' : 'Pickup / Drop-off Location'}
                        </label>
                        <Select onValueChange={(value) => setFormData({...formData, pickupLocation: value, dropoffLocation: formData.differentDropoff ? formData.dropoffLocation : value})}
                          value={formData.pickupLocation}>
                          <SelectTrigger className="font-body bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12 focus:border-[#10B981] focus:ring-[#10B981]">
                            <SelectValue placeholder={isRo ? 'Selectează locația' : 'Select location'} />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 rounded-lg shadow-lg">
                            {pickupLocations.map((loc) => (
                              <SelectItem key={loc.id} value={loc.id}
                                className="font-body text-gray-900 hover:bg-gray-100 focus:bg-gray-100 rounded-lg">
                                {language === 'en' ? loc.nameEn : loc.nameRo} {loc.price > 0 && `— €${loc.price}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <button type="button"
                          onClick={() => setFormData(prev => ({ ...prev, differentDropoff: !prev.differentDropoff, dropoffLocation: prev.differentDropoff ? prev.pickupLocation : prev.dropoffLocation }))}
                          className="mt-2 text-sm text-[#10B981] hover:underline font-medium">
                          {formData.differentDropoff
                            ? (isRo ? 'Aceeași locație pentru predare' : 'Same drop-off location')
                            : (isRo ? 'Altă locație pentru predare' : 'Different drop-off location')}
                        </button>

                        {formData.differentDropoff && (
                          <div className="mt-3">
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {isRo ? 'Locație Predare' : 'Drop-off Location'}
                            </label>
                            <Select onValueChange={(value) => setFormData({...formData, dropoffLocation: value})}
                              value={formData.dropoffLocation}>
                              <SelectTrigger className="font-body bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12 focus:border-[#10B981] focus:ring-[#10B981]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200 rounded-lg shadow-lg">
                                {pickupLocations.map((loc) => (
                                  <SelectItem key={loc.id} value={loc.id}
                                    className="font-body text-gray-900 hover:bg-gray-100 focus:bg-gray-100 rounded-lg">
                                    {language === 'en' ? loc.nameEn : loc.nameRo} {loc.price > 0 && `— €${loc.price}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>

                      {error && (
                        <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg">
                          <AlertCircle size={18} />
                          <span className="font-body text-sm">{error}</span>
                        </div>
                      )}

                      <Button onClick={goToStage2} data-testid="booking-stage1-cta"
                        className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                        {checkingAvailability
                          ? (isRo ? 'Se verifică...' : 'Checking...')
                          : (isRo ? 'Vezi Mașini Disponibile' : 'See Available Cars')}
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </div>
                  )}

                  {/* ===== STAGE 2: Car Selection & Insurance ===== */}
                  {stage === 2 && (
                    <div className="space-y-6">
                      <button type="button" onClick={() => { setStage(1); setError(''); scrollToBooking(); }}
                        className="flex items-center text-sm text-gray-500 hover:text-[#10B981] transition-colors mb-2">
                        <ArrowLeft size={16} className="mr-1" />
                        {isRo ? 'Înapoi la date' : 'Back to dates'}
                      </button>

                      <p className="text-gray-500 text-sm">
                        {formData.pickupDate && formData.returnDate && (
                          <>
                            {format(formData.pickupDate, 'dd MMM yyyy')} — {format(formData.returnDate, 'dd MMM yyyy')}
                            {' · '}
                            {Math.ceil(Math.abs(formData.returnDate - formData.pickupDate) / (1000 * 60 * 60 * 24))} {isRo ? 'zile' : 'days'}
                          </>
                        )}
                      </p>

                      {availableCars.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          {isRo ? 'Nu sunt mașini disponibile pentru aceste date.' : 'No cars available for these dates.'}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {availableCars.map((car) => {
                            const isSelected = formData.carId === car.id.toString();
                            const days = Math.ceil(Math.abs(formData.returnDate - formData.pickupDate) / (1000 * 60 * 60 * 24));
                            return (
                              <div key={car.id}
                                onClick={() => selectCar(car.id)}
                                data-testid={`car-card-${car.id}`}
                                className={`cursor-pointer rounded-xl border-2 transition-all overflow-hidden ${
                                  isSelected ? 'border-[#10B981] shadow-lg ring-2 ring-[#10B981]/20' : 'border-gray-200 hover:border-[#10B981]/50 hover:shadow-md'
                                }`}>
                                <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                                  <img src={car.image} alt={car.name} loading="lazy" className="w-full h-full object-cover" />
                                </div>
                                <div className="p-4">
                                  <div className="flex items-center justify-between mb-1">
                                    <h3 className="font-display text-base font-bold text-[#0D1B3E]">{car.name}</h3>
                                    {isSelected && <CheckCircle2 size={18} className="text-[#10B981]" />}
                                  </div>
                                  <p className="text-gray-400 text-xs mb-3">{car.category}</p>
                                  <div className="flex items-center gap-3 text-gray-500 text-xs mb-3">
                                    <span className="flex items-center gap-1"><Users size={13} /> {car.passengers}</span>
                                    <span className="flex items-center gap-1"><Briefcase size={13} /> {car.luggage}</span>
                                    <span>{car.transmission}</span>
                                    <span>{car.fuel}</span>
                                  </div>
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-[#10B981] font-bold text-lg">€{car.pricePerDay}</span>
                                    <span className="text-gray-400 text-xs">/{isRo ? 'zi' : 'day'}</span>
                                    {days > 0 && (
                                      <span className="text-gray-400 text-xs ml-auto">
                                        {isRo ? 'Total' : 'Total'}: €{car.pricePerDay * days}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Insurance - shown after car selection */}
                      {formData.carId && (
                        <div className="mt-6">
                          <label className="block font-body text-sm text-gray-600 mb-3 tracking-wide uppercase">
                            {t.booking.insurance}
                          </label>
                          <div className="space-y-3">
                            {Object.values(insurancePackages).map((pkg) => {
                              const selectedCar = cars.find(c => c.id === parseInt(formData.carId));
                              const depositAmount = selectedCar ? selectedCar.deposits[pkg.id] : 0;
                              const insurancePrice = selectedCar ? selectedCar.insurancePricing[pkg.id] : pkg.pricePerDay;
                              const isSelected = formData.insurancePackage === pkg.id;
                              return (
                                <div key={pkg.id}
                                  onClick={() => setFormData({...formData, insurancePackage: pkg.id})}
                                  data-testid={`insurance-${pkg.id}`}
                                  className={`flex items-center p-4 border cursor-pointer transition-all rounded-lg ${
                                    isSelected ? 'border-[#10B981] bg-[#10B981]/5' : 'border-gray-200 bg-gray-50 hover:border-[#10B981]'
                                  }`}>
                                  <Shield size={20} className={`mr-3 ${isSelected ? 'text-[#10B981]' : 'text-gray-400'}`} />
                                  <div className="flex-1">
                                    <p className="font-body text-gray-900 text-sm font-medium">
                                      {language === 'en' ? pkg.nameEn : pkg.nameRo} — €{insurancePrice}/{isRo ? 'zi' : 'day'}
                                    </p>
                                    <p className="font-body text-gray-500 text-xs mt-0.5">
                                      {language === 'en' ? pkg.descriptionEn : pkg.descriptionRo}
                                      {depositAmount > 0 && ` · ${isRo ? 'Depozit' : 'Deposit'}: €${depositAmount}`}
                                      {depositAmount === 0 && ` · ${isRo ? 'Fără depozit' : 'No deposit'}`}
                                    </p>
                                  </div>
                                  <div className={`w-4 h-4 border rounded-full flex items-center justify-center ${isSelected ? 'border-[#10B981]' : 'border-gray-300'}`}>
                                    {isSelected && <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {error && (
                        <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg">
                          <AlertCircle size={18} />
                          <span className="font-body text-sm">{error}</span>
                        </div>
                      )}

                      {formData.carId && (
                        <Button onClick={goToStage3} data-testid="booking-stage2-cta"
                          className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                          {isRo ? 'Continuă' : 'Continue'}
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      )}
                    </div>
                  )}

                  {/* ===== STAGE 3: Personal Info, Extras, Payment ===== */}
                  {stage === 3 && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <button type="button" onClick={() => { setStage(2); setError(''); scrollToBooking(); }}
                        className="flex items-center text-sm text-gray-500 hover:text-[#10B981] transition-colors mb-2">
                        <ArrowLeft size={16} className="mr-1" />
                        {isRo ? 'Înapoi la selecția mașinii' : 'Back to car selection'}
                      </button>

                      {/* Selected car summary */}
                      {formData.carId && (() => {
                        const car = cars.find(c => c.id === parseInt(formData.carId));
                        return car ? (
                          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <img src={car.image} alt={car.name} loading="lazy" className="w-20 h-14 object-cover rounded" />
                            <div>
                              <p className="font-bold text-[#0D1B3E] text-sm">{car.name}</p>
                              <p className="text-gray-500 text-xs">{car.category} · €{car.pricePerDay}/{isRo ? 'zi' : 'day'}</p>
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Personal Details */}
                      <div className="space-y-4">
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {t.booking.fullName}
                          </label>
                          <Input type="text" value={formData.fullName}
                            onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                            data-testid="input-fullname"
                            className="font-body bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-lg h-12 focus:border-[#10B981] focus:ring-[#10B981]"
                            placeholder={t.booking.fullName} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {t.booking.email}
                            </label>
                            <Input type="email" value={formData.email}
                              onChange={(e) => setFormData({...formData, email: e.target.value})}
                              data-testid="input-email"
                              className="font-body bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-lg h-12 focus:border-[#10B981] focus:ring-[#10B981]"
                              placeholder={t.booking.email} />
                          </div>
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {t.booking.phone}
                            </label>
                            <Input type="tel" value={formData.phone}
                              onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              data-testid="input-phone"
                              className="font-body bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-lg h-12 focus:border-[#10B981] focus:ring-[#10B981]"
                              placeholder={t.booking.phone} />
                          </div>
                        </div>
                      </div>

                      {/* Times */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {t.booking.pickupTime}
                          </label>
                          <Input type="time" value={formData.pickupTime}
                            onChange={(e) => setFormData({...formData, pickupTime: e.target.value})}
                            className="font-body bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12 focus:border-[#10B981] focus:ring-[#10B981]" />
                        </div>
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {t.booking.dropoffTime}
                          </label>
                          <Input type="time" value={formData.dropoffTime}
                            onChange={(e) => setFormData({...formData, dropoffTime: e.target.value})}
                            className="font-body bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12 focus:border-[#10B981] focus:ring-[#10B981]" />
                        </div>
                      </div>

                      {/* Extra Options */}
                      <div>
                        <label className="block font-body text-sm text-gray-600 mb-3 tracking-wide uppercase">
                          {t.booking.extraOptions}
                        </label>
                        <div className="space-y-3">
                          {extraOptions.map((option) => {
                            const isSelected = formData.selectedExtras.includes(option.id);
                            const toggleOption = () => {
                              if (isSelected) {
                                setFormData(prev => ({...prev, selectedExtras: prev.selectedExtras.filter(id => id !== option.id)}));
                              } else {
                                setFormData(prev => ({...prev, selectedExtras: [...prev.selectedExtras, option.id]}));
                              }
                            };
                            return (
                              <div key={option.id}
                                className="flex items-center space-x-4 p-4 bg-gray-50 border border-gray-200 hover:border-[#10B981] transition-colors cursor-pointer rounded-lg"
                                onClick={toggleOption}>
                                <div className={`w-4 h-4 border flex items-center justify-center rounded ${isSelected ? 'bg-[#10B981] border-[#10B981]' : 'border-gray-300'}`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <span className="font-body text-gray-900 text-sm">
                                    {language === 'en' ? option.nameEn : option.nameRo}
                                  </span>
                                  <span className="font-body text-[#10B981] text-sm ml-2">
                                    {option.price > 0 ? `€${option.price}` : (isRo ? 'Gratuit' : 'Free')}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Price Breakdown */}
                      {priceBreakdown && (
                        <div className="p-6 bg-[#0D1B3E] rounded-lg">
                          <h4 className="font-body text-sm text-white/60 tracking-wide uppercase mb-4">{t.booking.priceBreakdown}</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="font-body text-white/70 text-sm">{t.booking.carRental} ({priceBreakdown.days} {t.booking.days})</span>
                              <span className="font-body text-white text-sm">€{priceBreakdown.carTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="font-body text-white/70 text-sm">{t.booking.insuranceFee} ({priceBreakdown.days} {t.booking.days})</span>
                              <span className="font-body text-white text-sm">€{priceBreakdown.insuranceTotal.toFixed(2)}</span>
                            </div>
                            {priceBreakdown.locationFees > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="font-body text-white/70 text-sm">{t.booking.locationFees}</span>
                                <span className="font-body text-white text-sm">€{priceBreakdown.locationFees.toFixed(2)}</span>
                              </div>
                            )}
                            {priceBreakdown.extrasFees > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="font-body text-white/70 text-sm">{t.booking.extraOptionsFees}</span>
                                <span className="font-body text-white text-sm">€{priceBreakdown.extrasFees.toFixed(2)}</span>
                              </div>
                            )}
                            {priceBreakdown.deposit > 0 && (
                              <div className="flex justify-between items-center pt-3 border-t border-white/10">
                                <span className="font-body text-[#10B981] text-sm">{t.booking.deposit}</span>
                                <span className="font-body text-[#10B981] text-sm">€{priceBreakdown.deposit.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center pt-3 border-t border-white/10">
                              <span className="font-body text-white font-semibold">{t.booking.total}</span>
                              <span className="font-display text-[#10B981] text-2xl font-semibold">€{priceBreakdown.total.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Payment Method */}
                      <div>
                        <label className="block font-body text-sm text-gray-600 mb-3 tracking-wide uppercase">
                          {t.booking.paymentMethod}
                        </label>
                        <div className="space-y-3">
                          {Object.values(paymentMethods).map((method) => (
                            <div key={method.id}
                              onClick={() => setFormData({...formData, paymentMethod: method.id})}
                              className={`flex items-center p-4 border cursor-pointer transition-all rounded-lg ${
                                formData.paymentMethod === method.id ? 'border-[#10B981] bg-[#10B981]/5' : 'border-gray-200 bg-gray-50 hover:border-[#10B981]'
                              }`}>
                              <div className="mr-4 text-[#10B981]">
                                {method.id === 'stripe' && <CreditCard size={20} strokeWidth={1.5} />}
                                {method.id === 'bank' && <Building size={20} strokeWidth={1.5} />}
                              </div>
                              <div className="flex-1">
                                <p className="font-body text-gray-900 text-sm font-medium">{language === 'en' ? method.nameEn : method.nameRo}</p>
                                <p className="font-body text-gray-500 text-xs mt-0.5">{language === 'en' ? method.descriptionEn : method.descriptionRo}</p>
                              </div>
                              <div className={`w-4 h-4 border rounded-full flex items-center justify-center ${formData.paymentMethod === method.id ? 'border-[#10B981]' : 'border-gray-300'}`}>
                                {formData.paymentMethod === method.id && <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {error && (
                        <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg">
                          <AlertCircle size={18} />
                          <span className="font-body text-sm">{error}</span>
                        </div>
                      )}

                      <Button type="submit" disabled={loading} data-testid="booking-submit"
                        className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        {loading
                          ? (isRo ? 'Se procesează...' : 'Processing...')
                          : (isRo ? 'Trimite Cererea de Rezervare' : 'Submit Booking Request')}
                      </Button>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSystem;
